/* eslint-disable react/no-unknown-property */
/* eslint-disable react/jsx-props-no-spreading */
import React, { useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Typography, Button, Link } from '@one-thd/sui-atomic-components';
import { Info } from '@one-thd/sui-icons';
import { ExperienceContext, useConfigService } from '@thd-nucleus/experience-context';
import { useImpression, ImpressionProvider } from '@thd-olt-component-react/impression';
import { CARD_IMAGE } from '../utils/constants';
import { formatPrice, getDynamicOffer } from '../utils/general';
import { getDefaultApplyNowURL, loadEstimatorScript, clickApplyNow } from '../utils/payment-estimator-utils';
import TermsAndConditionsDrawer from '../common/terms-and-conditions-drawer.component';
import ProXtraDrawer from '../common/pro-xtra-drawer.component';

function NoImpact({
  isB2B,
  total,
  hideDynamicMath,
  saleAmount,
  pageName,
  isNested,
  onCloseParentDrawer
}) {

  const experienceContext = useContext(ExperienceContext);
  const paymentEstimatorVersion = useConfigService('PaymentEstimatorVersion') || 'v1.8.1';
  const [applyNowLink, setApplyNowUrl] = useState('');
  const [showDrawer, setShowDrawer] = useState(false);

  const { channel, isConsumerApp } = experienceContext;
  const isMobile = channel === 'mobile';
  const featureName = isNested ? 'plcc ATC banner' : 'plcc';
  const moduleName = isNested ? 'buy box' : 'right rail';

  const printValues = (title, value, bold = false, success = false, negative = false) => {
    return (
      <div
        className={`sui-flex sui-flex-row sui-justify-between sui-mt-2 ${success && 'sui-text-success'
          }`}
      >
        <div className={bold ? 'sui-font-w-bold' : ''}>{title}</div>
        <div className={bold ? 'sui-font-w-bold' : ''}>
          {negative && '-'}
          {formatPrice(value)}
        </div>
      </div>
    );
  };

  const cardImage = isB2B ? CARD_IMAGE.PLCR_HOMEDEPOT : CARD_IMAGE.PLCN_HOMEDEPOT;

  const updateApplyNowUrl = () => {
    let saleAmountParam = saleAmount ? '&saleAmt=' + saleAmount : '';
    let sc = isB2B ? '' : '&sc=30065';
    setApplyNowUrl(getDefaultApplyNowURL(isMobile, pageName, isB2B) + saleAmountParam + sc);
  };

  useEffect(() => {
    loadEstimatorScript(
      paymentEstimatorVersion,
      updateApplyNowUrl,
      channel === 'mobile'
    );
  }, []);

  const { ref, clickID } = useImpression({
    data: {
      id: '00DEFVALPLCCNI00',
      name: 'NoImpact',
      component: 'NoImpact',
      position: 1,
      type: 'content',
    }
  });

  return (
    <div className="sui-flex sui-flex-col sui-mt-4" ref={ref} clickid={clickID}>
      {isB2B
        ? (
          <ProXtraDrawer
            applyNowClick={() => clickApplyNow(featureName, moduleName, isMobile, applyNowLink)}
            closeDrawer={() => setShowDrawer(false)}
            open={showDrawer}
            isNested={isNested}
            onCloseParentDrawer={onCloseParentDrawer}
          />
        )
        : (
          <TermsAndConditionsDrawer
            closeDrawer={() => setShowDrawer(false)}
            open={showDrawer}
            isB2B={false}
            price={total}
            isNoImpact
            applyNowClick={() => clickApplyNow(featureName, moduleName, isMobile, applyNowLink)}
            isNested={isNested}
            onCloseParentDrawer={onCloseParentDrawer}
          />
        )}

      <div className="sui-flex">

        <Typography variant="h4" weight="display">
          {isB2B
            ? <>Earn Perks <span className="sui-text-brand">4X</span> Faster^</>
            : 'You Could Save Up To $100 Today*'}
        </Typography>
      </div>

      <div className="sui-mt-2 sui-text-base">
        {isB2B ? (
          <>
            Earn ${(Math.floor((Number(total))) * 4).toLocaleString()} in{' '}
            Perks Spend on a ${Math.floor(total).toLocaleString()}{' '}
            qualifying purchase when you join Pro Xtra, register and use your card.{' '}

            <span className="sui-align-text-bottom">
              <Link
                component="button"
                color="brand"
                onClick={() => setShowDrawer(!showDrawer)}
              >
                Learn More
              </Link>
            </span>
          </>
        ) : (
          <>
            See if you pre-qualify with no impact to your credit score.
            <span className="sui-pl-1 sui-cursor-pointer sui-relative sui-top-1">
              <Info
                aria-label="Credit Offer Details"
                color="info"
                onClick={() => setShowDrawer(!showDrawer)}
                size="small"
              />
            </span>
          </>
        )}
      </div>

      <div id="applyNowLink" className="sui-mb-4 sui-mt-4 sui-flex sui-gap-3">
        <img
          src={cardImage}
          width="auto"
          height="auto"
          alt="Credit Card Icon"
          className="sui-h-11 sui-mr-2"
        />
        <Button
          onClick={() => clickApplyNow(featureName, moduleName, isConsumerApp, applyNowLink)}
          fullWidth
          variant="secondary"
        >
          {isB2B ? 'Apply Now' : 'Check Now'}
        </Button>
      </div>

      {
        total >= 25 && !hideDynamicMath && (
          <div id="values" className=" sui-mb-3">
            {printValues('Item Total', total, true)}
            {printValues('Savings', getDynamicOffer(total), false, false, true)}
            {printValues('New Total', total - getDynamicOffer(total), true, true)}
          </div>
        )
      }
    </div>
  );
}

NoImpact.propTypes = {
  isB2B: PropTypes.bool,
  total: PropTypes.number.isRequired,
  hideDynamicMath: PropTypes.bool,
  saleAmount: PropTypes.number,
  pageName: PropTypes.string,
  isNested: PropTypes.bool,
  onCloseParentDrawer: PropTypes.func
};

NoImpact.defaultProps = {
  isB2B: false,
  hideDynamicMath: false,
  saleAmount: 0,
  pageName: 'noimpact',
  isNested: false,
  onCloseParentDrawer: null
};
const NoImpactWrapper = (props) => {

  return (

    <ImpressionProvider data={{
      id: '',
      name: 'ConsumerCardMessaging',
      component: 'ConsumerCardMessaging',
      type: 'content',
      position: 1,
    }}
    >
      <NoImpact {...props} />
    </ImpressionProvider>

  );

};

export { NoImpactWrapper as NoImpact };
